import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Button } from '../ui/button';

const productItems = [
  { label: 'Overview', path: '/product', description: 'Platform capabilities' },
  { label: 'Control Tower', path: '/control-tower', description: 'Operations dashboard' },
  { label: 'Resolve Requests', path: '/product/resolve-requests', description: 'Request intake & routing' },
  { label: 'Access Management', path: '/product/access-management', description: 'Identity lifecycle' },
  { label: 'Automation Builder', path: '/product/automation-builder', description: 'Workflows & policies' },
  { label: 'Knowledge', path: '/product/knowledge', description: 'Knowledge base & Q&A' },
  { label: 'Endpoints', path: '/endpoints', description: 'Device compliance' },
  { label: 'SaaS Governance', path: '/saas', description: 'Application access & spend' },
  { label: 'Security Response', path: '/security', description: 'Threat containment' },
  { label: 'Asset Graph', path: '/assets', description: 'Inventory & relationships' },
  { label: 'Configuration Intelligence', path: '/services', description: 'Drift detection' },
  { label: 'Cost Intelligence', path: '/cost', description: 'Spend attribution' },
  { label: 'Ticketing & ITSM', path: '/product/itsm', description: 'Service desk integration' },
];

const resourceItems = [
  { label: 'Documentation', path: '/docs', description: 'Guides and API reference' },
  { label: 'Templates', path: '/templates', description: 'Pre-built workflows' },
  { label: 'Comparisons', path: '/comparisons', description: 'Category analysis' },
];

export const MarketingHeader = () => {
  const location = useLocation();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const toggleDropdown = (name) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  const NavDropdown = ({ name, label, items, gridCols = 1 }) => (
    <div className="relative" ref={activeDropdown === name ? dropdownRef : null}>
      <button
        onClick={() => toggleDropdown(name)}
        className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors ${
          activeDropdown === name
            ? 'text-primary'
            : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === name ? 'rotate-180' : ''}`} />
      </button>
      {activeDropdown === name && (
        <div className={`absolute left-0 top-full mt-2 bg-white rounded-lg border border-border shadow-lg py-2 z-50 ${
          gridCols === 2 ? 'w-[480px]' : 'w-64'
        }`}>
          <div className={gridCols === 2 ? 'grid grid-cols-2 gap-1 p-2' : ''}>
            {items.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="block px-3 py-2 hover:bg-secondary rounded-md transition-colors"
              >
                <div className="text-sm font-medium text-foreground">{item.label}</div>
                <div className="text-xs text-muted-foreground">{item.description}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-border">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-14">
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-xs">V</span>
              </div>
              <span className="text-base font-semibold text-foreground">Vedaya</span>
            </Link>

            <nav className="hidden lg:flex items-center" ref={dropdownRef}>
              <NavDropdown name="product" label="Product" items={productItems} gridCols={2} />
              <Link
                to="/integrations"
                className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Integrations
              </Link>
              <Link
                to="/trust"
                className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Security
              </Link>
              <NavDropdown name="resources" label="Resources" items={resourceItems} />
              <Link
                to="/pricing"
                className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Pricing
              </Link>
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex">
              <Link to="/control-tower">Log In</Link>
            </Button>
            <Button asChild size="sm">
              <Link to="/demo/request">Request Demo</Link>
            </Button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-secondary transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border">
            <div className="space-y-1">
              <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Product</div>
              {productItems.slice(0, 6).map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="block px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary rounded-md"
                >
                  {item.label}
                </Link>
              ))}
              <div className="border-t border-border my-2" />
              <Link to="/integrations" className="block px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary rounded-md">
                Integrations
              </Link>
              <Link to="/trust" className="block px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary rounded-md">
                Security
              </Link>
              <Link to="/pricing" className="block px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary rounded-md">
                Pricing
              </Link>
              <div className="border-t border-border my-2" />
              <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Resources</div>
              {resourceItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="block px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary rounded-md"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
